import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  CircularProgress,
  MenuItem,
  Checkbox,
  Autocomplete,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LanguageIcon from '@mui/icons-material/Language';

const PreguntasPredefinidas = ({ 
  open, 
  onClose, 
  user,
  contextOptions = [],
  predefinedQuestions = [],
  preguntaVariables = [],
  loadingQuestions = false,
  onRefreshData,
  showOnlyActive,
  setShowOnlyActive
}) => {
  // Estados locales del componente
  const [showPreguntaForm, setShowPreguntaForm] = useState(false);
  const [editingPregunta, setEditingPregunta] = useState(null);
  const [preguntaToDelete, setPreguntaToDelete] = useState(null);
  const [selectedContextIds, setSelectedContextIds] = useState([]);
  const [contextPopoverAnchor, setContextPopoverAnchor] = useState(null);
  const [selectedPreguntaContextos, setSelectedPreguntaContextos] = useState([]);
  
  const [preguntaForm, setPreguntaForm] = useState({
    pregunta: "",
    variable: "",
    tipo_dato: "texto",
    activa: true,
    prompt: "",
    es_global: false,
    es_borrable: true,
  });

  // Función para guardar pregunta
  const handleSavePregunta = async (forzar = false, payloadOverride = null) => {
    const variableRegex = /^[a-z_][a-z0-9_]*$/;
    const sinAcentos = (str) => !/[áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙäëïöüÄËÏÖÜñÑ]/.test(str);
    if (!variableRegex.test(preguntaForm.variable) || !sinAcentos(preguntaForm.variable)) {
      alert("El nombre de la variable debe estar en minúsculas, sin espacios, sin acentos, no puede empezar por número ni contener mayúsculas.");
      return;
    }

    const url = editingPregunta
      ? `/api/preguntas_predefinidas/${editingPregunta.id}`
      : "/api/preguntas_predefinidas/";
    const method = editingPregunta ? "PUT" : "POST";
    const userId = user?.id || localStorage.getItem("user_id");

    const payload = payloadOverride || { ...preguntaForm, contextos: selectedContextIds, es_borrable: preguntaForm.es_borrable !== false };

    const res = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "X-User-Id": String(userId)
      },
      body: JSON.stringify(payload)
    });

    if (res.ok) {
      onRefreshData();
      setShowPreguntaForm(false);
      setEditingPregunta(null);
      setPreguntaForm({ pregunta: "", variable: "", tipo_dato: "texto", activa: true, prompt: "", es_global: false, es_borrable: true });
      setSelectedContextIds([]);
    } else {
      let error = { detail: "Error al guardar la pregunta" };
      try {
        error = await res.json();
      } catch {
        error = { detail: "Error al guardar la pregunta" };
      }
      alert((error && error.detail) || "Error al guardar la pregunta");
    }
  };

  // Función para eliminar pregunta
  const handleDeletePregunta = (pregunta) => {
    setPreguntaToDelete(pregunta);
  };

  const confirmDeletePregunta = async () => {
    if (!preguntaToDelete) return;
    const userId = user?.id || localStorage.getItem("user_id");
    const res = await fetch(`/api/preguntas_predefinidas/${preguntaToDelete.id}`, {
      method: "DELETE",
      headers: {
        "X-User-Id": String(userId)
      }
    });
    if (res.ok) {
      onRefreshData();
      setPreguntaToDelete(null);
    } else {
      const error = await res.json();
      alert(error.detail || "Error al eliminar la pregunta");
      setPreguntaToDelete(null);
    }
  };

  const cancelDeletePregunta = () => {
    setPreguntaToDelete(null);
  };

  const handleClose = () => {
    setShowPreguntaForm(false);
    setEditingPregunta(null);
    setPreguntaForm({ pregunta: "", variable: "", tipo_dato: "texto", activa: true, prompt: "", es_global: false, es_borrable: true });
    setSelectedContextIds([]);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: {
                xs: "99vw",
                sm: "98vw",
                md: "700px",
                lg: "750px",
              },
              maxWidth: "99vw"
            }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: 18, py: 1.5 }}>
          Gestionar preguntas predefinidas
        </DialogTitle>
        
        <DialogContent sx={{ p: 1.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showOnlyActive}
                onChange={e => setShowOnlyActive(e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="Mostrar solo activas"
            sx={{ mb: 1 }}
          />



         <Box sx={{ maxHeight: 260, overflowY: "auto", width: "100%", position: "relative" }}>
  {loadingQuestions && (
    <Box sx={{
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      bgcolor: "rgba(255,255,255,0.7)",
      zIndex: 2,
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    }}>
      <CircularProgress size={24} />
    </Box>
  )}

  <Box sx={{ opacity: loadingQuestions ? 0.9 : 1 }}>
    <Table size="small" sx={{
      tableLayout: "fixed",
      width: "100%",
      minWidth: 500,
      "& th, & td": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        fontSize: { xs: 11, sm: 12, md: 13 }
      }
    }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ p: 0.3, maxWidth: 110, width: 110 }}>Pregunta</TableCell>
          <TableCell sx={{ p: 0.3, width: 55 }}>Var</TableCell>
          <TableCell sx={{ p: 0.3, width: 55 }}>Tipo</TableCell>
          <TableCell sx={{ p: 0.3, width: 50 }}>Activa</TableCell>
          <TableCell sx={{ p: 0.3, width: 60 }}>Borrable</TableCell>
          <TableCell sx={{ p: 0.3, width: 70 }}>Contextos</TableCell>
          <TableCell sx={{ p: 0.3, width: 80 }} align="right">Acciones</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {predefinedQuestions
          .sort((a, b) => a.id - b.id)
          .map(q => {
            const contextosIds = preguntaVariables
              .filter(rel => rel.id_pregunta === q.id)
              .map(rel => rel.id_contexto);

            return (
              <TableRow key={q.id} hover>
                <TableCell sx={{ p: 0.3, maxWidth: 110 }}>
                  <span title={q.pregunta}>{q.pregunta}</span>
                </TableCell>
                <TableCell sx={{ p: 0.3 }}>{q.variable}</TableCell>
                <TableCell sx={{ p: 0.3 }}>{q.tipo_dato}</TableCell>
                <TableCell sx={{ p: 0.3 }}>
                  <Chip
                    label={q.activa ? "Sí" : "No"}
                    color={q.activa ? "success" : "default"}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ p: 0.3 }}>
                  <Switch
                    checked={q.es_borrable !== false}
                    size="small"
                    color="success"
                    onChange={async (e) => {
                      const userId = user?.id || localStorage.getItem("user_id");
                      const contextosIds = preguntaVariables
                        .filter(rel => rel.id_pregunta === q.id)
                        .map(rel => rel.id_contexto);

                      const payload = {
                        pregunta: q.pregunta,
                        variable: q.variable,
                        tipo_dato: q.tipo_dato,
                        activa: q.activa,
                        prompt: q.prompt,
                        es_global: q.es_global,
                        es_borrable: e.target.checked,
                        contextos: contextosIds
                      };

                      await fetch(`/api/preguntas_predefinidas/${q.id}`, {
                        method: "PUT",
                        headers: {
                          "Content-Type": "application/json",
                          "X-User-Id": String(userId)
                        },
                        body: JSON.stringify(payload)
                      });
                      onRefreshData();
                    }}
                    inputProps={{ 'aria-label': 'Cambiar borrable' }}
                  />
                </TableCell>
                <TableCell sx={{ p: 0.3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        const contextosAsociados = contextOptions.filter(opt =>
                          contextosIds.includes(opt.id)
                        );
                        setSelectedPreguntaContextos(contextosAsociados);
                        setContextPopoverAnchor(event.currentTarget);
                      }}
                      sx={{
                        color: contextosIds.length > 0 ? "#6366f1" : "#cbd5e1",
                        '&:hover': {
                          background: contextosIds.length > 0 ? "#e0e7ef" : "#f1f5f9"
                        }
                      }}
                      title={`Ver ${contextosIds.length} contexto(s)`}
                    >
                      <LanguageIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                      {contextosIds.length}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ p: 0.3 }} align="right">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setEditingPregunta(q);
                      setPreguntaForm({
                        ...q,
                        es_borrable: q.es_borrable !== false
                      });
                      const contextosIds = preguntaVariables
                        .filter(rel => rel.id_pregunta === q.id)
                        .map(rel => rel.id_contexto);
                      setSelectedContextIds(contextosIds);
                      setShowPreguntaForm(true);
                    }}
                  >
                    <EditNoteIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeletePregunta(q)}
                    disabled={q.es_borrable === false}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  </Box>
</Box>

          {(showPreguntaForm || editingPregunta) && (
            <Box sx={{ p: 1, mt: 1, mb: 1, borderRadius: 2, background: "#f8fafc", width: { xs: "99vw", sm: "98vw", md: "700px" } }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                {editingPregunta ? "Editar" : "Nueva"} pregunta
              </Typography>
              
              <Grid container spacing={1}>
                <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Switch 
                    checked={preguntaForm.activa} 
                    onChange={e => setPreguntaForm(f => ({ ...f, activa: e.target.checked }))} 
                    color="primary" 
                    size="small" 
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={preguntaForm.es_borrable !== false}
                        onChange={e => setPreguntaForm(f => ({ ...f, es_borrable: e.target.checked }))}
                        color="primary"
                        size="small"
                      />
                    }
                    label="¿Es borrable?"
                    sx={{ mb: 1 }}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    label="Pregunta"
                    size="small"
                    value={preguntaForm.pregunta}
                    onChange={e => setPreguntaForm(f => ({ ...f, pregunta: e.target.value }))}
                    required
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={6} md={3}>
                  <TextField
                    label="Variable"
                    size="small"
                    value={preguntaForm.variable}
                    onChange={e => {
                      let val = e.target.value
                        .replace(/[^a-z0-9_]/g, "")
                        .replace(/[áéíóúàèìòùäëïöüñÁÉÍÓÚÀÈÌÒÙÄËÏÖÜÑ]/g, "");
                      if (val.length === 1 && /[0-9]/.test(val[0])) val = "";
                      setPreguntaForm(f => ({ ...f, variable: val }));
                    }}
                    required
                    error={
                      !!preguntaForm.variable &&
                      predefinedQuestions.some(
                        q => q.variable === preguntaForm.variable && 
                        (!editingPregunta || q.id !== editingPregunta.id)
                      )
                    }
                    helperText={(() => {
                      if (!preguntaForm.variable) return "";
                      const preguntaRepetida = predefinedQuestions.find(
                        q => q.variable === preguntaForm.variable && 
                        (!editingPregunta || q.id !== editingPregunta.id)
                      );
                      return preguntaRepetida
                        ? `Ya está en uso por: "${preguntaRepetida.pregunta}"`
                        : "";
                    })()}
                    sx={{ width: '100%' }}
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <TextField 
                    select 
                    label="Tipo" 
                    size="small" 
                    value={preguntaForm.tipo_dato} 
                    onChange={e => setPreguntaForm(f => ({ ...f, tipo_dato: e.target.value }))} 
                    fullWidth 
                    required
                  >
                    <MenuItem value="texto">Texto</MenuItem>
                    <MenuItem value="entero">Entero</MenuItem>
                    <MenuItem value="decimal">Decimal</MenuItem>
                    <MenuItem value="fecha">Fecha</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Prompt de validación"
                    size="small"
                    required
                    multiline
                    minRows={5}
                    maxRows={5}
                    value={preguntaForm.prompt}
                    onChange={e => setPreguntaForm(f => ({ ...f, prompt: e.target.value }))}
                    sx={{ width: '100%' }}
                    inputProps={{ style: { overflowY: 'auto', resize: 'none' } }}
                  />
                </Grid>    
                <Grid item xs={12} md={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={preguntaForm.es_global || false}
                        onChange={e => {
                          const checked = e.target.checked;
                          setPreguntaForm(f => ({ ...f, es_global: checked }));
                          if (checked) {
                            setSelectedContextIds(contextOptions.map(opt => opt.id));
                          } else {
                            setSelectedContextIds([]);
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Pregunta global (aplica a todos los contextos)"
                    sx={{ mb: 1 }}
                  />
                  <Autocomplete
                    multiple
                    options={contextOptions}
                    getOptionLabel={option => option.nombre}
                    value={contextOptions.filter(opt => selectedContextIds.includes(opt.id))}
                    onChange={(e, value) => setSelectedContextIds(value.map(opt => opt.id))}
                    renderInput={params => (
                      <TextField 
                        {...params} 
                        label="Contextos" 
                        placeholder="Selecciona contextos" 
                        size="small" 
                      />
                    )}
                    disabled={preguntaForm.es_global}
                  />
                </Grid>
              </Grid>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 1 }}>
                <Button 
                  size="small" 
                  variant="outlined" 
                  onClick={() => { 
                    setShowPreguntaForm(false); 
                    setEditingPregunta(null); 
                    setPreguntaForm({ pregunta: "", variable: "", tipo_dato: "texto", activa: true, prompt: "", es_global: false, es_borrable: true }); 
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSavePregunta(false)}
                  disabled={
                    !preguntaForm.pregunta.trim() ||
                    !preguntaForm.variable.trim() ||
                    (!!preguntaForm.variable && predefinedQuestions.some(
                      q => q.variable === preguntaForm.variable && 
                      (!editingPregunta || q.id !== editingPregunta.id)
                    ))
                  }
                  color="primary"
                >
                  Guardar
                </Button>
              </Box>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button 
              size="small" 
              variant="contained" 
              color="success" 
              onClick={() => { 
                setEditingPregunta(null); 
                setPreguntaForm({ pregunta: "", variable: "", tipo_dato: "texto", activa: true, prompt: "", es_global: false, es_borrable: true }); 
                setShowPreguntaForm(true); 
              }}
            >
              + Nueva pregunta
            </Button>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" onClick={handleClose}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preguntaToDelete} onClose={cancelDeletePregunta}>
        <DialogTitle sx={{ color: "rgba(23,66,136,0.98)", fontWeight: 800 }}>
          ¿Eliminar pregunta?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: "rgba(23,66,136,0.98)", fontWeight: 500, fontSize: 16 }}>
            Esta acción no se puede deshacer.<br />
            ¿Deseas eliminar la siguiente pregunta?
            <Box sx={{ mt: 2, p: 2, background: '#f5f5f5', borderRadius: 1 }}>
              <b>{preguntaToDelete?.pregunta}</b>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeletePregunta} color="primary">
            Cancelar
          </Button>
          <Button onClick={confirmDeletePregunta} color="error" variant="contained">
            Sí, eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={Boolean(contextPopoverAnchor)}
        anchorEl={contextPopoverAnchor}
        onClose={() => {
          setContextPopoverAnchor(null);
          setSelectedPreguntaContextos([]);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 250,
            maxHeight: 280,
            borderRadius: 2,
            boxShadow: "0 4px 16px rgba(99,102,241,0.15)",
            background: "#f8fafc",
            p: 1,
          }
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ 
            fontWeight: 700, 
            color: "#334155", 
            mb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <LanguageIcon sx={{ fontSize: 18, color: "#6366f1" }} />
            Contextos aplicables ({selectedPreguntaContextos.length})
          </Typography>
          {selectedPreguntaContextos.length === 0 ? (
            <Box sx={{ 
              p: 1.5,
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.8
            }}>
              <CircularProgress size={18} sx={{ color: "#6366f1", opacity: 0.7 }} />
              <Typography variant="body2" sx={{ color: "#64748b", fontStyle: "italic", fontSize: 13 }}>
                Esta pregunta no tiene contextos asignados
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {selectedPreguntaContextos
                .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' }))
                .map((contexto, index) => {
                const iconColors = ["#6366f1", "#f472b6", "#34d399", "#fb7185", "#a78bfa", "#fbbf24"];
                const colorIndex = index % iconColors.length;
                const iconColor = iconColors[colorIndex];
                return (
                  <ListItem
                    key={contexto.id}
                    sx={{
                      borderRadius: 1,
                      m: 0.3,
                      py: 0.5,
                      transition: "all 0.2s",
                      "&:hover": {
                        background: "#e0e7ef",
                      },
                      cursor: "pointer",
                      position: "relative",
                    }}
                    dense
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <LanguageIcon sx={{ fontSize: 16, color: iconColor }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={contexto.nombre} 
                      primaryTypographyProps={{ 
                        fontSize: 12,
                        fontWeight: 500,
                        color: "#334155",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default PreguntasPredefinidas;